import { useQuery } from '@tanstack/react-query';
import { API } from '@/utils/API';
import { StudyData } from '@/types/study';

// Attribute keys accepted by GET /api/v1/studies for corpus-backed filtering
// (attribute + attribute_q). Keep in sync with the backend allowlist.
export const STUDY_ATTRIBUTE_KEYS = [
  'cro',
  'sponsor',
  'pi',
  'pi_email',
  'poc',
  'poc_email',
  'poc_phone',
  'poc_role',
] as const;

export type StudyAttributeKey = (typeof STUDY_ATTRIBUTE_KEYS)[number];

export const isStudyAttributeKey = (
  value: unknown
): value is StudyAttributeKey =>
  typeof value === 'string' &&
  (STUDY_ATTRIBUTE_KEYS as readonly string[]).includes(value);

// Server-side sort keys for the corpus-backed HEAD attribute columns
// (alphabetical by corpus value, NULLS LAST on the backend).
export type StudyHeadSortKey = 'sort_cro' | 'sort_sponsor' | 'sort_pi';

export interface StudiesPagination {
  total: number;
  page?: number;
  per_page?: number;
  total_pages?: number;
}

export interface UseStudiesListParams {
  search?: string;
  status?: string;
  type?: string;
  order?: string;
  sort?: string;
  attribute?: StudyAttributeKey | '';
  attributeQuery?: string;
  /** 1-indexed. Omit (with perPage) to fetch every matching row, as today. */
  page?: number;
  perPage?: number;
}

interface StudiesResponse {
  resources: StudyData[];
  pagination: StudiesPagination;
}

export const buildStudiesSearchParams = (
  params: UseStudiesListParams
): URLSearchParams => {
  const searchParams = new URLSearchParams({
    order: params.order || 'desc',
    sort: params.sort || 'created_at',
    search: params.search || '',
    status: params.status || '',
    type: params.type || '',
  });

  // Only send the attribute filter when both halves are present — the backend
  // matches attribute_q against the corpus for the selected attribute.
  if (params.attribute && params.attributeQuery) {
    searchParams.set('attribute', params.attribute);
    searchParams.set('attribute_q', params.attributeQuery);
  }

  if (params.page) {
    searchParams.set('page', String(params.page));
    searchParams.set('per_page', String(params.perPage || 25));
  }

  return searchParams;
};

export const fetchStudies = async (
  params: UseStudiesListParams
): Promise<StudiesResponse> => {
  const searchParams = buildStudiesSearchParams(params);

  const response = await API.authenticated.fetch(
    `/studies?${searchParams.toString()}`
  );

  if (!response.ok) {
    throw new Error('Failed to fetch studies');
  }

  return response.json();
};

// `data` stays the plain resources array — the shape every existing consumer
// (Studies/Show.tsx's site list, ConnectSiteModal, ScopedSitesTable) already
// expects — with `pagination` surfaced alongside it for callers (the Studies
// index page) that opt into `page`/`perPage` and need the total page count.
export const useStudiesList = (params: UseStudiesListParams) => {
  const query = useQuery({
    queryKey: ['studies', params],
    queryFn: () => fetchStudies(params),
    staleTime: 2 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    ...query,
    data: query.data?.resources,
    pagination: query.data?.pagination,
  };
};
